import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { useSaveMarketingPlan } from '../../hooks/backend/marketing'
import { writeErrorMessage } from '../../utils/writeError'
import { firstOfMonth, type CodeLabel, type MarketingPlan } from '../../utils/marketing'

/**
 * A content plan: one month of intent, with a name.
 *
 * The month is normalised to the first, in the database as well as here, because
 * a plan that straddles two months cannot be reported on against either.
 *
 * The objective is optional. Requiring a sentence produces "grow the brand",
 * which is worse than a blank, because a blank is an honest record of a plan
 * whose objective was never stated.
 */
export function PlanDialog({
  open,
  onOpenChange,
  plan,
  audiences,
  onSaved,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  plan: MarketingPlan | null
  audiences: CodeLabel[]
  onSaved: () => void
}) {
  const save = useSaveMarketingPlan()
  const [month, setMonth] = useState('')
  const [title, setTitle] = useState('')
  const [objective, setObjective] = useState('')
  const [status, setStatus] = useState('Active')
  const [feedSize, setFeedSize] = useState('')
  const [canvas, setCanvas] = useState('')
  const [principles, setPrinciples] = useState('')
  // Keyed on the audience code. The database refuses a set that does not sum to
  // 100, so this is validated here only to say so before the click.
  const [targets, setTargets] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    if (plan) {
      setMonth(plan.period_month.slice(0, 10))
      setTitle(plan.title)
      setObjective(plan.objective ?? '')
      setStatus(plan.status)
      setFeedSize(plan.feed_size === null ? '' : String(plan.feed_size))
      setCanvas(plan.canvas_default ?? '')
      setPrinciples(plan.principles ?? '')
      setTargets(
        Object.fromEntries(
          (plan.mix ?? [])
            .filter((m) => m.target_share !== null)
            .map((m) => [m.audience, String(m.target_share)]),
        ),
      )
    } else {
      const now = new Date()
      setMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`)
      setTitle('')
      setObjective('')
      setStatus('Active')
      setFeedSize('')
      setCanvas('')
      setPrinciples('')
      setTargets({})
    }
  }, [open, plan])

  async function submit() {
    setBusy(true)
    try {
      const targetRows = Object.entries(targets)
        .map(([audience, v]) => ({ audience, target_share: Number(v) }))
        .filter((t) => Number.isFinite(t.target_share) && t.target_share > 0)

      await save.trigger({
        id: plan?.id ?? null,
        period_month: firstOfMonth(month),
        title,
        objective: objective || null,
        status,
        feed_size: feedSize === '' ? null : Number(feedSize),
        canvas_default: canvas || null,
        principles: principles || null,
        // Undefined leaves the mix alone; an array replaces it whole. A mix is a
        // set, and merging half of one into another produces a set nobody chose.
        targets: targetRows.length > 0 ? targetRows : undefined,
      }).result
      toast.success(plan ? 'Plan updated' : 'Plan created')
      onOpenChange(false)
      onSaved()
    } catch (err) {
      toast.error('The plan was not saved', { description: writeErrorMessage(err) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{plan ? 'Edit this plan' : 'New content plan'}</DialogTitle>
          <DialogDescription>
            A month of intent. More than one plan can share a month, for a standing plan and a
            campaign running beside it, but not two with the same name.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="pl-month" className="text-xs">
              Month
            </Label>
            <Input
              id="pl-month"
              type="date"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="h-9"
            />
            <p className="text-[11px] text-muted-foreground">
              Any day in the month is fine; it is stored as the first.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pl-title" className="text-xs">
              Name
            </Label>
            <Input
              id="pl-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="September standing plan"
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pl-obj" className="text-xs">
              What this month is for
            </Label>
            <Textarea
              id="pl-obj"
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
              rows={3}
              className="text-sm"
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="pl-feed" className="text-xs">
                Posts in the month
              </Label>
              {/* Every share on this screen is a share of this number. Without
                  it a percentage has no denominator and nobody can check it. */}
              <Input
                id="pl-feed"
                type="number"
                value={feedSize}
                onChange={(e) => setFeedSize(e.target.value)}
                placeholder="20"
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pl-canvas" className="text-xs">
                Usual size
              </Label>
              <Input
                id="pl-canvas"
                value={canvas}
                onChange={(e) => setCanvas(e.target.value)}
                placeholder="1080 x 1350"
                className="h-9"
              />
            </div>
          </div>

          {/* THE MIX. A plan that states the split it wants is a plan somebody
              can be held to; without these rows nobody counts the headings and
              a month runs 85/15 while the document says 80/20. */}
          <div className="space-y-1.5">
            <Label className="text-xs">The mix it should be</Label>
            <div className="space-y-2 rounded-md border border-border bg-muted/30 p-2.5">
              {audiences.map((a) => (
                <div key={a.code} className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 text-xs text-foreground">{a.label}</span>
                  <Input
                    type="number"
                    value={targets[a.code] ?? ''}
                    onChange={(e) =>
                      setTargets((t) => ({ ...t, [a.code]: e.target.value }))
                    }
                    placeholder="0"
                    aria-label={`${a.label} target share`}
                    className="h-8 w-20 text-xs"
                  />
                  <span className="text-xs text-muted-foreground">%</span>
                  {feedSize !== '' && targets[a.code] && (
                    <span className="w-20 text-right text-[11px] text-muted-foreground">
                      {Math.round((Number(targets[a.code]) * Number(feedSize)) / 100)} posts
                    </span>
                  )}
                </div>
              ))}
              {(() => {
                const sum = Object.values(targets).reduce((n, v) => n + (Number(v) || 0), 0)
                if (sum === 0) {
                  return (
                    <p className="text-[11px] text-muted-foreground">
                      Leave these empty for a plan with no stated mix.
                    </p>
                  )
                }
                return (
                  <p
                    className={
                      sum === 100
                        ? 'text-[11px] text-muted-foreground'
                        : 'text-[11px] text-amber-700 dark:text-amber-300'
                    }
                  >
                    {sum}% of the month allocated
                    {sum === 100 ? '' : '. It has to add up to 100 before this will save.'}
                  </p>
                )
              })()}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pl-principles" className="text-xs">
              Creative principles
            </Label>
            {/* Held on the plan rather than in a separate document, because the
                person writing a caption at six in the evening does not open a
                separate document. */}
            <Textarea
              id="pl-principles"
              value={principles}
              onChange={(e) => setPrinciples(e.target.value)}
              rows={4}
              placeholder="One strong idea per creative. Do not present performance targets as guaranteed earnings."
              className="text-sm"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Status</Label>
            <NativeSelect
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="h-9"
            >
              <option value="Draft">Draft</option>
              <option value="Active">Active</option>
              <option value="Closed">Closed</option>
            </NativeSelect>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="brand"
            size="sm"
            className="gap-1.5"
            disabled={
              title.trim() === '' ||
              busy ||
              (() => {
                const sum = Object.values(targets).reduce((n, v) => n + (Number(v) || 0), 0)
                return sum !== 0 && sum !== 100
              })()
            }
            onClick={() => void submit()}
          >
            {busy && <Spinner className="h-3.5 w-3.5" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
